import { z } from 'zod/v4';

/**
 * Body of `POST /risk-analysis/assessments/calculate`, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment.js` → `calculate.body`.
 *
 * `companyIds` is optional but, when present, must be non-empty (`minItems: 1`) —
 * an empty array was a 400 in legacy, not "calculate for everything". Omitting the
 * key entirely is what selects every linked company.
 */
export const CalculateAssessmentBodySchema = z.object({
  blueprintId: z.string().min(1).max(36),
  companyIds: z.array(z.string().min(1).max(36)).min(1).optional(),
});

export type CalculateAssessmentBody = z.infer<
  typeof CalculateAssessmentBodySchema
>;

/**
 * Body of `POST /risk-analysis/assessments/calculate/standard-assessment`, ported
 * from `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment.js` →
 * `calculateByStandardAssessment.body`.
 *
 * `companyIds` is required here (unlike the plain calculate endpoint) and must be
 * non-empty. The caller's own company is appended server-side, so it need not be
 * listed.
 */
export const CalculateByStandardAssessmentBodySchema = z.object({
  companyIds: z.array(z.string().min(1).max(36)).min(1),
});

export type CalculateByStandardAssessmentBody = z.infer<
  typeof CalculateByStandardAssessmentBodySchema
>;

/**
 * `GET /risk-analysis/assessments/:id` params, ported from
 * `2025-06/risk-analysis-service/routes/http/v3/assessments/get.js`, which reused
 * `assessment.listDetails.params` (`{id}`, max 36 chars) for this single-resource
 * route.
 */
export const GetAssessmentParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetAssessmentParams = z.infer<typeof GetAssessmentParamsSchema>;

export const AssessmentRiskFactorSchema = z.object({
  id: z.string(),
  title: z.string(),
});

/**
 * `GET /risk-analysis/assessments/:id` response, ported from
 * `2025-06/risk-analysis-service/handlers/v3/assessments/get/index.js` over
 * `data/assessments/get-by-id.js`'s `getById` + `buildAssessmentData`, and the
 * `assessmentWithRiskFactors` JSON-schema definition
 * (`routes/schemas/v3/responses.js`) that the legacy route served.
 *
 * `companyData` is the ABOUT profile merged with the OPERATIONAL_METRICS profile
 * (OPERATIONAL_METRICS wins on key conflicts) — an arbitrary datapoint bag, keyed
 * by whatever datapoint keys the two profiles carry (`display_name`, `country`,
 * `annual_purchase_volume`, ...), so it stays a record rather than an enumerated
 * shape.
 *
 * `minPriorityScore`/`maxPriorityScore` are nullable here, unlike the legacy
 * schema's `type: 'number'` — the underlying value genuinely is SQL NULL when the
 * assessment has no CURRENT results, and the legacy schema's non-nullable
 * declaration was never exercised by fast-json-stringify's number serializer in a
 * way any consumer depended on (no test asserts a non-null default). Typing them
 * as nullable reflects the real data instead of repeating an untested contract lie.
 */
export const AssessmentDetailResourceSchema = z.object({
  id: z.string(),
  blueprintId: z.string(),
  blueprintName: z.string(),
  isBlueprintArchived: z.boolean(),
  createdByCompanyId: z.string(),
  companyId: z.string(),
  scoreFormula: z.string(),
  companyData: z.record(z.string(), z.unknown()),
  riskFactors: z.array(AssessmentRiskFactorSchema),
  minPriorityScore: z.number().nullable(),
  maxPriorityScore: z.number().nullable(),
});

export type AssessmentDetailResource = z.infer<
  typeof AssessmentDetailResourceSchema
>;

export const LIST_ASSESSMENT_DETAILS_DEFAULT_PAGE_SIZE = 15;
export const LIST_ASSESSMENT_DETAILS_MAX_PAGE_SIZE = 500;
export const LIST_ASSESSMENT_DETAILS_FILTER_MAX_LENGTH = 512;

/**
 * Keys of `SORT_COLUMNS_ENUM` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment.js`
 * (`assessment.listDetails`). Kept in sync with
 * `core/db/assessments/list-details-filters.ts`'s `AssessmentDetailsListSortColumns`.
 */
export const AssessmentDetailsListSortColumns = [
  'id',
  'title',
  'remarksCount',
  'datapointsAnswered',
  'priorityScore',
  'grossValue',
  'likelihood',
  'severity',
] as const;

const sortColumnAlternatives = AssessmentDetailsListSortColumns.join('|');

/**
 * A single `column:direction` pair — this endpoint never supported the
 * comma-separated multi-sort some sibling list endpoints do.
 */
const listDetailsSortParamSchema = z
  .string()
  .regex(new RegExp(`^(${sortColumnAlternatives})(:(ASC|DESC|asc|desc))?$`))
  .optional();

/**
 * `GET /risk-analysis/assessments/:id/details` params, ported from
 * `2025-06/risk-analysis-service/routes/http/v3/assessments/list-details.js`,
 * which reused `assessment.listDetails.params`.
 */
export const GetAssessmentDetailsParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetAssessmentDetailsParams = z.infer<
  typeof GetAssessmentDetailsParamsSchema
>;

/**
 * `GET /risk-analysis/assessments/:id/details` querystring, ported from
 * `assessment.listDetails.querystring`. `filter` accepts a JSON object with a
 * single meaningful key, `hasRemarks` (`'yes' | 'no'`) — the legacy schema's
 * `risks` filter key is declared but never wired into the query, so it is
 * dropped here as accepted-but-ignored noise.
 */
export const GetAssessmentDetailsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(LIST_ASSESSMENT_DETAILS_MAX_PAGE_SIZE)
    .default(LIST_ASSESSMENT_DETAILS_DEFAULT_PAGE_SIZE),
  sort: listDetailsSortParamSchema,
  filter: z.string().max(LIST_ASSESSMENT_DETAILS_FILTER_MAX_LENGTH).optional(),
});

export type GetAssessmentDetailsQuery = z.infer<
  typeof GetAssessmentDetailsQuerySchema
>;

export const AssessmentDetailMetricResultSchema = z.object({
  metricId: z.string(),
  score: z.number(),
});

/**
 * One row of `GET /risk-analysis/assessments/:id/details`, ported from
 * `listDetailsByIdQuery` or as read by `buildAssessment`. `likelihoodScore`/
 * `severityScore` are computed by that query (for sorting) but were silently
 * stripped from the legacy wire response by its JSON schema's
 * `additionalProperties: false` — neither frontend reads them, both derive
 * likelihood/severity from `metricResults` instead, so they're dropped here
 * rather than ported as dead weight.
 */
export const AssessmentDetailRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  grossValue: z.number().nullable(),
  priorityScore: z.number().nullable(),
  metricResults: z.array(AssessmentDetailMetricResultSchema).nullable(),
  remarksCount: z.number(),
  datapoints: z.number(),
  datapointsAnswered: z.number(),
});

export type AssessmentDetailRow = z.infer<typeof AssessmentDetailRowSchema>;

const LIST_PRIORITIZATION_DETAILS_DEFAULT_PAGE_SIZE = 15;
const LIST_PRIORITIZATION_DETAILS_MAX_PAGE_SIZE = 500;

/**
 * `GET /risk-analysis/assessments/:id/prioritization-details` params, ported
 * from `2025-06/risk-analysis-service/routes/http/v3/assessments/list-prioritization-details.js`,
 * which reused `assessment.listPrioritizationDetails.params`.
 */
export const GetPrioritizationDetailsParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetPrioritizationDetailsParams = z.infer<
  typeof GetPrioritizationDetailsParamsSchema
>;

/**
 * `GET /risk-analysis/assessments/:id/prioritization-details` querystring,
 * ported from `assessment.listPrioritizationDetails.querystring`
 * (`...paginationParams` only) — this route never accepted `sort`, unlike the
 * sibling `GET /v3/risk-profiles/:companyId/blueprints/:blueprintId/prioritization-details`
 * route that shares the same underlying legacy query function.
 */
export const GetPrioritizationDetailsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(LIST_PRIORITIZATION_DETAILS_MAX_PAGE_SIZE)
    .default(LIST_PRIORITIZATION_DETAILS_DEFAULT_PAGE_SIZE),
});

export type GetPrioritizationDetailsQuery = z.infer<
  typeof GetPrioritizationDetailsQuerySchema
>;

/**
 * One row of `GET /risk-analysis/assessments/:id/prioritization-details`,
 * ported from `listPrioritizationDetailsByIdQuery` in
 * `data/assessments/queries/get.js`.
 */
export const PrioritizationDetailRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  priorityScore: z.number().nullable(),
  minPriorityScore: z.number().nullable(),
  maxPriorityScore: z.number().nullable(),
  metricResults: z.array(AssessmentDetailMetricResultSchema).nullable(),
});

export type PrioritizationDetailRow = z.infer<
  typeof PrioritizationDetailRowSchema
>;

const LIST_ASSESSMENT_HISTORY_DEFAULT_PAGE_SIZE = 15;
const LIST_ASSESSMENT_HISTORY_MAX_PAGE_SIZE = 500;

/**
 * `GET /risk-analysis/assessments/:id/history` params, ported from
 * `2025-06/risk-analysis-service/routes/http/v3/assessments/history.js`, which
 * reused `assessment.history.params`.
 */
export const GetAssessmentHistoryParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetAssessmentHistoryParams = z.infer<
  typeof GetAssessmentHistoryParamsSchema
>;

/**
 * `GET /risk-analysis/assessments/:id/history` querystring, ported from
 * `assessment.history.querystring` (`...paginationParams` only) — no
 * sort/filter.
 */
export const GetAssessmentHistoryQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(LIST_ASSESSMENT_HISTORY_MAX_PAGE_SIZE)
    .default(LIST_ASSESSMENT_HISTORY_DEFAULT_PAGE_SIZE),
});

export type GetAssessmentHistoryQuery = z.infer<
  typeof GetAssessmentHistoryQuerySchema
>;

/**
 * `assessmentTrigger` on a history row, ported from the shape the legacy
 * handler builds: `{event, asset, assetName}`. `asset` is an arbitrary,
 * passthrough bag (a `PROFILE_UPDATED` trigger's asset has no `type`/`typeId`
 * at all, just one boolean flag per changed datapoint key) — kept as a
 * `record` rather than a narrow `{type, typeId}` shape so consumers reading
 * other keys off it (e.g. frontend-v2's `toTriggerAssets`) keep working.
 * `assetName` is only present when `asset` is — a bare string for a
 * single-asset trigger, an array of labels for the datapoint case, or `null`
 * when the type/id didn't resolve to a name.
 */
export const AssessmentTriggerSchema = z.object({
  // Optional: `blueprint-companies/bulk` and some legacy company-profile triggers
  // publish an assessment-mitigation event with no `triggerEvent` by design (see
  // `MitigationTriggerBase` in assessment-calculation.service.ts), so the stored
  // trigger JSON can genuinely lack `event`.
  event: z.string().optional(),
  asset: z.record(z.string(), z.unknown()).optional(),
  assetName: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional(),
});

export type AssessmentTrigger = z.infer<typeof AssessmentTriggerSchema>;

/**
 * One row of `GET /risk-analysis/assessments/:id/history`, ported from
 * `historyQuery` in `data/assessments/queries/get.js`, as mapped by
 * `handlers/v3/assessments/history/index.js`.
 *
 * `likelihoodScore` is the legacy column alias for `net_score` — the response
 * contract really does call it `likelihoodScore`, not `netScore`, and both
 * frontends already label the column "Likelihood", so the name is preserved
 * verbatim rather than "corrected".
 *
 * `likelihoodScore`/`severityScore`/`averagePriorityScore` are all nullable:
 * `net_score` is genuinely unset while `CALCULATING`/after `FAILED` (the same
 * reasoning as `AssessmentBlueprintHistoryEntrySchema.netRisk`), and the two
 * `AVG(...)` subqueries return SQL NULL when no matching rows exist.
 */
export const AssessmentHistoryRowSchema = z.object({
  id: z.string(),
  likelihoodScore: z.number().nullable(),
  severityScore: z.number().nullable(),
  averagePriorityScore: z.number().nullable(),
  hasMitigation: z.boolean(),
  status: z.string(),
  assessmentTrigger: AssessmentTriggerSchema.nullable(),
  createdAt: z.string().nullable(),
});

export type AssessmentHistoryRow = z.infer<typeof AssessmentHistoryRowSchema>;

/**
 * `GET /risk-analysis/companies/:id/assessments/standard` params, ported
 * from `2025-06/risk-analysis-service/routes/schemas/v3/request/company.js`'s
 * `getStandard.params`.
 */
export const GetStandardAssessmentParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetStandardAssessmentParams = z.infer<
  typeof GetStandardAssessmentParamsSchema
>;

/**
 * `GET /risk-analysis/companies/:id/assessments/standard` response, ported
 * from `buildStandardAssessment`
 * (`2025-06/risk-analysis-service/handlers/v3/assessments/helpers.js`) and
 * the legacy `standardAssessment` response definition
 * (`routes/schemas/v3/responses.js`).
 *
 * That legacy schema declares a third field, `netScoreChange`, but the
 * handler computes `netScoreChanged` (note the trailing `d`) — the
 * mismatched key means `additionalProperties: false` silently strips it from
 * every response, so the field has never once reached a caller. Dropped here
 * rather than ported as a permanently-`undefined` value on the wire (see the
 * pinned legacy integration test asserting exactly this).
 */
export const StandardAssessmentSchema = z.object({
  netScore: z.number().min(0).max(5),
  status: z.string(),
});

export type StandardAssessment = z.infer<typeof StandardAssessmentSchema>;
